/** Query keys for everything under a graph, in one place. */

import { queryClient } from '@/lib/query.client'

export const brainKeys = {
  graphs: ['brain', 'graphs'] as const,
  explorerStats: (id: string) => ['brain', 'explorer-stats', id] as const,
  explorerQueries: (id: string) => ['brain', 'explorer-queries', id] as const,
  explorerLimits: (id: string) => ['brain', 'explorer-limits', id] as const,
  graph: (id: string) => ['brain', 'graph', id] as const,
  model: (id: string) => ['brain', 'model', id] as const,
  stats: (id: string) => ['brain', 'stats', id] as const,
  nodeTypes: (id: string) => ['brain', 'node-types', id] as const,
  nodeType: (id: string, nodeTypeId: string) => ['brain', 'node-type', id, nodeTypeId] as const,
  edgeTypes: (id: string) => ['brain', 'edge-types', id] as const,
  dqOperators: (id: string) => ['brain', 'dq-operators', id] as const,
  dqCompliance: (id: string) => ['brain', 'dq-compliance', id] as const,
  matchMethods: (id: string) => ['brain', 'match-methods', id] as const,
  reviews: (id: string, status: string) => ['brain', 'reviews', id, status] as const,
  strategies: (id: string) => ['brain', 'strategies', id] as const,
  audit: (id: string, entityId?: string) => ['brain', 'audit', id, entityId ?? 'all'] as const,
  records: (id: string, params: unknown) => ['brain', 'records', id, params] as const,
  recordCounts: (id: string) => ['brain', 'record-counts', id] as const,
  relationships: (id: string, params: unknown) =>
    ['brain', 'relationships', id, params] as const,
  relationshipSummary: (id: string) => ['brain', 'relationship-summary', id] as const,
  linkReport: (id: string) => ['brain', 'link-report', id] as const,
  record: (id: string, entityId: string) => ['brain', 'record', id, entityId] as const,
  staged: (id: string, params: unknown) => ['brain', 'staged', id, params] as const,
  ego: (id: string, entityId: string, depth: number) =>
    ['brain', 'ego', id, entityId, depth] as const,
  lineage: (id: string, entityId: string) => ['brain', 'lineage', id, entityId] as const,
  objectTables: (id: string) => ['brain', 'object-tables', id] as const,
  objectRows: (id: string, nodeTypeId: string, params: unknown) =>
    ['brain', 'object-rows', id, nodeTypeId, params] as const,
  sources: (id: string) => ['brain', 'sources', id] as const,
  agents: (id: string) => ['brain', 'agents', id] as const,
  pipelineStatus: (id: string) => ['brain', 'pipeline-status', id] as const,
  pipelineRuns: (id: string) => ['brain', 'pipeline-runs', id] as const,
  search: (id: string, q: string) => ['brain', 'search', id, q] as const,
}

/**
 * Refresh everything a schema edit can move.
 *
 * Editing one node type changes the canvas, the rail, the tables and the
 * counts, so these invalidate together rather than leaving one view stale.
 */
export function invalidateModel(graphId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: brainKeys.model(graphId) }),
    queryClient.invalidateQueries({ queryKey: brainKeys.nodeTypes(graphId) }),
    queryClient.invalidateQueries({ queryKey: brainKeys.edgeTypes(graphId) }),
    queryClient.invalidateQueries({ queryKey: ['brain', 'node-type', graphId] }),
    queryClient.invalidateQueries({ queryKey: brainKeys.stats(graphId) }),
    queryClient.invalidateQueries({ queryKey: brainKeys.graphs }),
    // A model edit creates, alters or drops a physical table.
    queryClient.invalidateQueries({ queryKey: brainKeys.objectTables(graphId) }),
  ])
}

/** Refresh anything the pipeline can change. */
export function invalidateData(graphId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['brain', 'records', graphId] }),
    queryClient.invalidateQueries({ queryKey: ['brain', 'staged', graphId] }),
    queryClient.invalidateQueries({ queryKey: brainKeys.objectTables(graphId) }),
    queryClient.invalidateQueries({ queryKey: ['brain', 'object-rows', graphId] }),
    queryClient.invalidateQueries({ queryKey: brainKeys.pipelineStatus(graphId) }),
    queryClient.invalidateQueries({ queryKey: brainKeys.pipelineRuns(graphId) }),
    queryClient.invalidateQueries({ queryKey: brainKeys.stats(graphId) }),
    queryClient.invalidateQueries({ queryKey: ['brain', 'reviews', graphId] }),
    queryClient.invalidateQueries({ queryKey: brainKeys.sources(graphId) }),
    queryClient.invalidateQueries({ queryKey: brainKeys.agents(graphId) }),
    // Relationships are data the pipeline creates, so they move with it.
    queryClient.invalidateQueries({ queryKey: ['brain', 'relationships', graphId] }),
    queryClient.invalidateQueries({ queryKey: brainKeys.relationshipSummary(graphId) }),
    queryClient.invalidateQueries({ queryKey: brainKeys.linkReport(graphId) }),
    queryClient.invalidateQueries({ queryKey: brainKeys.recordCounts(graphId) }),
    queryClient.invalidateQueries({ queryKey: ['brain', 'explorer-stats', graphId] }),
  ])
}
